import type { PipelineRun } from "../sse";
import { Pill } from "./ai/Primitives";
import { IconDownload, IconPage } from "./icons";

/**
 * Body of a clerk-triggered pipeline run, shown as a window in the right-column
 * stack (the PanelWindow frame supplies the title + close). Shows the run status
 * + artifact links: a Google Doc link for a gdoc render, otherwise a presigned
 * .docx download, plus an optional PDF. (The hosted run is buffered, so this
 * shows the completed run; live per-node progress is a later enhancement.)
 */
export function PipelineRunPanel({ run }: { run: PipelineRun }) {
  const arts = run.artifacts || [];
  const ok = run.status === "done" && !run.error;
  const dim = "var(--ink-3)";
  return (
    <div style={{ fontSize: 13 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Pill tone={ok ? "done" : run.error ? "error" : "running"} dot={!ok && !run.error}>
          {ok ? "Done" : run.error ? "Failed" : "Running"}
        </Pill>
        {run.pipeline_id && <span style={{ color: dim }}>{run.pipeline_id}</span>}
      </div>
      <div>{/* artifacts */}
        {run.error && (
          <div style={{ color: "var(--red)", whiteSpace: "pre-wrap", marginBottom: 10 }}>{run.error}</div>
        )}
        {!run.error && arts.length === 0 && (
          <div style={{ color: dim }}>
            {run.status === "done"
              ? "No artifacts produced."
              : "Run failed — no detail returned. See the chat reply for the error."}
          </div>
        )}
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {arts.map((a, i) => (
            <li key={i}>
              <a
                href={a.url}
                target="_blank"
                rel="noreferrer"
                {...(a.kind === "gdoc" ? {} : { download: a.name })}
                style={{ color: "var(--accent)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                {a.kind === "gdoc" ? <IconPage size={13} /> : <IconDownload size={13} />}{a.name}
                {a.bytes ? <span style={{ color: dim }}>{Math.round(a.bytes / 1024)} KB</span> : null}
                {a.kind === "gdoc" ? <span style={{ color: dim }}>Opens in Google Docs</span> : null}
              </a>
              {a.pdf_url && (
                <> · <a href={a.pdf_url} target="_blank" rel="noreferrer" download
                  style={{ color: "var(--accent)" }}>PDF</a></>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
